// @ts-check
/** @typedef {import('../types/api').Expense} Expense */
/** @typedef {import('../types/api').ExpenseSummary} ExpenseSummary */

import { DollarSign, ExternalLink, FileText, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import { useToast } from '../context/ToastContext';
import expenseService from '../services/expenseService';

const ExpensesPage = () => {
  const { showToast } = useToast();
  /** @type {[Expense[], Function]} */
  const [expenses, setExpenses] = useState([]);
  /** @type {[ExpenseSummary | null, Function]} */
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    start_date: '',
    end_date: '',
  });

  const expenseCategories = [
    { value: 'vendor_payment', label: 'Vendor Payment' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'operations', label: 'Operations' },
    { value: 'inventory', label: 'Inventory' },
    { value: 'salaries', label: 'Salaries' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'rent', label: 'Rent' },
    { value: 'other', label: 'Other' },
  ];

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [expensesData, summaryData] = await Promise.all([
        expenseService.getExpenses(filters),
        expenseService.getExpenseSummary(filters.start_date, filters.end_date),
      ]);
      setExpenses(expensesData);
      setSummary(summaryData);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewVendorOrder = async (expenseId) => {
    try {
      const data = await expenseService.getExpenseVendorOrder(expenseId);
      if (data.vendor_order_id) {
        window.open(`/vendors/orders/${data.vendor_order_id}`, '_blank');
      } else {
        showToast(data.message, 'info');
      }
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const getCategoryLabel = (category) => {
    const found = expenseCategories.find((c) => c.value === category);
    return found ? found.label : category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      vendor_payment: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300',
      marketing: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300',
      operations: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
      inventory: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
      salaries: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
      utilities: 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-300',
      rent: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300',
      other: 'bg-muted text-foreground',
    };
    return colors[category] || colors.other;
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">Expense Tracker</h1>
        <p className="text-muted-foreground mt-1">Track and manage business expenses</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-card rounded-lg shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Expenses</p>
                <p className="text-3xl font-bold text-foreground">₹{summary.total_expenses.toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900/40 p-3 rounded-lg">
                <DollarSign className="text-blue-600 dark:text-blue-400" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Tax Paid</p>
                <p className="text-3xl font-bold text-foreground">₹{summary.total_tax.toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-green-100 dark:bg-green-900/40 p-3 rounded-lg">
                <FileText className="text-green-600 dark:text-green-400" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-card rounded-lg shadow p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Transactions</p>
                <p className="text-3xl font-bold text-foreground">{summary.expense_count}</p>
              </div>
              <div className="bg-purple-100 dark:bg-purple-900/40 p-3 rounded-lg">
                <TrendingUp className="text-purple-600 dark:text-purple-400" size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              <option value="">All Categories</option>
              {expenseCategories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Expenses List */}
      {loading ? (
        <LoadingScreen />
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Tax
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {expenses.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                      No expenses found
                    </td>
                  </tr>
                ) : (
                  expenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-muted/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {new Date(expense.expense_date).toLocaleDateString('en-IN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 text-xs rounded-full ${getCategoryColor(expense.category)}`}>
                          {getCategoryLabel(expense.category)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        <div className="max-w-xs truncate">{expense.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground text-right">
                        ₹{parseFloat(String(expense.amount)).toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground text-right">
                        ₹{parseFloat(String(expense.tax_amount)).toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-foreground text-right">
                        ₹
                        {(parseFloat(String(expense.amount)) + parseFloat(String(expense.tax_amount))).toLocaleString(
                          'en-IN',
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {expense.vendor_payment_id && (
                          <button
                            onClick={() => handleViewVendorOrder(expense.id)}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition"
                            title="View Vendor Order"
                          >
                            <ExternalLink size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {summary && summary.category_breakdown && (
        <div className="mt-6 bg-card rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Category Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(summary.category_breakdown).map(([category, amount]) => (
              <div key={category} className="border rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">{getCategoryLabel(category)}</p>
                <p className="text-xl font-bold text-foreground">₹{parseFloat(amount).toLocaleString('en-IN')}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
